import React, { useCallback, useEffect, useState } from 'react'
import { css } from '@emotion/core'
import { useTranslation } from 'react-i18next'
import { useForm, useWatch } from 'react-hook-form'
import {
  createRestaurantDisplaySubject,
  fetchRestaurantDisplaySubjects,
  fetchSalesSubjectMaster,
  updateRestaurantDisplaySubject,
  updateRestaurantDisplaySubjectOrder,
} from '@/apis/aipass'
import { SettingsLayout } from '@/components/layouts/settings-layout'
import { EditFooter } from '@/components/organisms/edit-footer'
import { TabContainer } from '@/components/organisms/settings/tab-container'
import { DisplaySubjectModal } from '@/components/organisms/settings/restaurant/display-subject-modal'
import { DisplaySubjectPane } from '@/components/organisms/settings/restaurant/display-subject-pane'
import { SettingRestaurantTab } from '@/components/organisms/settings/restaurant/tab'
import { useErrorHandler } from '@/hooks/use-error-handler'
import { useSettingsEditGuard } from '@/hooks/use-settings-edit-guard'
import type {
  RestaurantDisplaySubject,
  RestaurantDisplaySubjectOrderUpdateRequest,
  RestaurantResolvedDisplaySubject,
} from '@/models/restaurant-manager'
import type { SalesSubjectMasterType } from '@/models/sales-manager/sales'

type DisplaySubjectModalState = { mode: 'create' } | { mode: 'edit'; subject: RestaurantResolvedDisplaySubject }

export const SettingRestaurantDisplaySubject: React.FC = () => {
  const { t } = useTranslation()
  const { errorHandler } = useErrorHandler()
  const { isSettingsReadOnly, guardSettingsEdit } = useSettingsEditGuard()
  const [loading, setLoading] = useState(true)
  const [savedDisplaySubjects, setSavedDisplaySubjects] = useState<RestaurantResolvedDisplaySubject[] | null>(null)
  const [salesSubjectMasters, setSalesSubjectMasters] = useState<SalesSubjectMasterType[]>([])
  const [hasLoadedSalesSubjectMasters, setHasLoadedSalesSubjectMasters] = useState(false)
  const [isLoadingSalesSubjectMasters, setIsLoadingSalesSubjectMasters] = useState(false)
  const [displaySubjectModalState, setDisplaySubjectModalState] = useState<DisplaySubjectModalState | null>(null)
  const {
    control,
    formState: { isDirty },
    getValues,
    handleSubmit,
    reset,
  } = useForm<{ displaySubjects: RestaurantResolvedDisplaySubject[] }>({
    defaultValues: {
      displaySubjects: [],
    },
  })
  const displaySubjects = useWatch({ control, name: 'displaySubjects' }) ?? []

  const initializeComponent = useCallback(async () => {
    try {
      setLoading(true)
      const displaySubjects = await fetchRestaurantDisplaySubjects()
      setSavedDisplaySubjects(displaySubjects)
      reset({ displaySubjects: [...displaySubjects] })
    } catch (error) {
      errorHandler(error)
    } finally {
      setLoading(false)
    }
  }, [errorHandler, reset])

  useEffect(() => {
    initializeComponent()
  }, [initializeComponent])

  const saveDisplaySubjects = handleSubmit(async value => {
    if (!guardSettingsEdit()) return
    try {
      setLoading(true)
      await updateRestaurantDisplaySubjectOrder(toDisplaySubjectOrderUpdateRequest(value.displaySubjects))
      await initializeComponent()
    } catch (error) {
      errorHandler(error)
    } finally {
      setLoading(false)
    }
  })

  const saveDisplaySubjectFromModal = useCallback(
    async (nextSubject: Pick<RestaurantDisplaySubject, 'subjectId' | 'displayDay'>) => {
      if (!displaySubjectModalState) {
        return
      }

      try {
        setLoading(true)

        if (displaySubjectModalState.mode === 'edit') {
          await updateRestaurantDisplaySubject(displaySubjectModalState.subject.subjectId, nextSubject)
        } else {
          await createRestaurantDisplaySubject(nextSubject)
        }

        const refreshedDisplaySubjects = await fetchRestaurantDisplaySubjects()
        setSavedDisplaySubjects(refreshedDisplaySubjects)
        reset({ displaySubjects: refreshedDisplaySubjects })
        setDisplaySubjectModalState(null)
      } catch (error) {
        errorHandler(error)
      } finally {
        setLoading(false)
      }
    },
    [displaySubjectModalState, errorHandler, reset],
  )

  const cancelDisplaySubjects = useCallback(() => {
    if (!savedDisplaySubjects) {
      return
    }

    reset({ displaySubjects: savedDisplaySubjects })
  }, [reset, savedDisplaySubjects])

  const ensureSalesSubjectMasters = useCallback(async () => {
    if (hasLoadedSalesSubjectMasters) {
      return salesSubjectMasters
    }

    setIsLoadingSalesSubjectMasters(true)

    try {
      const masters = (await fetchSalesSubjectMaster()) ?? []
      setSalesSubjectMasters(masters)
      setHasLoadedSalesSubjectMasters(true)
      return masters
    } catch (error) {
      errorHandler(error)
      return []
    } finally {
      setIsLoadingSalesSubjectMasters(false)
    }
  }, [errorHandler, hasLoadedSalesSubjectMasters, salesSubjectMasters])

  const handleAddDisplaySubject = useCallback(() => {
    if (!guardSettingsEdit()) return
    setDisplaySubjectModalState({ mode: 'create' })

    if (!hasLoadedSalesSubjectMasters && !isLoadingSalesSubjectMasters) {
      void ensureSalesSubjectMasters()
    }
  }, [guardSettingsEdit, ensureSalesSubjectMasters, hasLoadedSalesSubjectMasters, isLoadingSalesSubjectMasters])

  const handleEditDisplaySubject = useCallback(
    (subject: RestaurantResolvedDisplaySubject) => {
      if (!guardSettingsEdit()) return
      setDisplaySubjectModalState({ mode: 'edit', subject })

      if (!hasLoadedSalesSubjectMasters && !isLoadingSalesSubjectMasters) {
        void ensureSalesSubjectMasters()
      }
    },
    [guardSettingsEdit, ensureSalesSubjectMasters, hasLoadedSalesSubjectMasters, isLoadingSalesSubjectMasters],
  )

  const handleDeleteDisplaySubject = useCallback(
    (subjectId: string) => {
      if (!guardSettingsEdit()) return
      reset(
        { displaySubjects: getValues('displaySubjects').filter(subject => subject.subjectId !== subjectId) },
        { keepDefaultValues: true },
      )
    },
    [guardSettingsEdit, getValues, reset],
  )

  const footerContent = isDirty ? <EditFooter onSave={saveDisplaySubjects} onCancel={cancelDisplaySubjects} /> : undefined

  const modalUsedSubjectIds =
    savedDisplaySubjects && displaySubjectModalState
      ? savedDisplaySubjects
          .filter(
            subject =>
              subject.subjectId !== (displaySubjectModalState.mode === 'edit' ? displaySubjectModalState.subject.subjectId : undefined),
          )
          .map(subject => subject.subjectId)
      : []

  const content = !savedDisplaySubjects ? (
    <div css={statusStyle}>{t('No data available')}</div>
  ) : (
    <DisplaySubjectPane
      value={displaySubjects}
      canAdd={
        !isSettingsReadOnly &&
        (!hasLoadedSalesSubjectMasters ||
          salesSubjectMasters.some(subject => !savedDisplaySubjects.some(item => item.subjectId === subject.id)))
      }
      canEditSubjects={!isSettingsReadOnly && !isLoadingSalesSubjectMasters}
      onAdd={handleAddDisplaySubject}
      onEdit={handleEditDisplaySubject}
      onDelete={handleDeleteDisplaySubject}
      onChange={nextList => reset({ displaySubjects: nextList }, { keepDefaultValues: true })}
    />
  )

  return (
    <>
      {savedDisplaySubjects && displaySubjectModalState && (
        <DisplaySubjectModal
          salesSubjectMasters={salesSubjectMasters}
          usedSubjectIds={modalUsedSubjectIds}
          editingSubject={displaySubjectModalState.mode === 'edit' ? displaySubjectModalState.subject : null}
          isLoadingOptions={isLoadingSalesSubjectMasters}
          onCancel={() => setDisplaySubjectModalState(null)}
          onSave={saveDisplaySubjectFromModal}
        />
      )}
      <SettingsLayout loading={loading} footerContent={footerContent}>
        <TabContainer tabComponent={<SettingRestaurantTab currentTab="RestaurantManager.DisplaySubjects" />} isShowFooter={isDirty}>
          {content}
        </TabContainer>
      </SettingsLayout>
    </>
  )
}

const statusStyle = css({
  alignItems: 'center',
  color: '#676767',
  display: 'flex',
  fontSize: 18,
  justifyContent: 'center',
  minHeight: '240px',
  padding: 32,
})

const toDisplaySubjectOrderUpdateRequest = (
  displaySubjects: RestaurantResolvedDisplaySubject[],
): RestaurantDisplaySubjectOrderUpdateRequest => ({
  displaySubjects: displaySubjects.map(subject => ({
    subjectId: subject.subjectId,
    displayDay: subject.displayDay,
  })),
})
